import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { RootStore } from "../Store";

function authorizationDenied(error: unknown): boolean {
  const status = (error as { statusCode?: number; response?: { status?: number } })?.statusCode ??
    (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403 ||
    /\b(401|403|unauthorized|forbidden)\b/i.test(String(error));
}

export class SignalrClient {
  private hubConnection: HubConnection | null = null;
  private opening: Promise<HubConnection | null> | null = null;
  private retryHandle: number | null = null;
  private retryDelay = 2000;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  get connection(): HubConnection | null {
    return this.hubConnection;
  }

  async restartConnection(): Promise<HubConnection | null> {
    await this.closeConnection();
    return this.openConnection();
  }

  async openConnection(): Promise<HubConnection | null> {
    if (this.opening) return this.opening;
    const store = this.rootStore.casesStore;
    if (!store.isCaseLoaded || !store.authToken) return null;
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected &&
        this.hubConnection.state !== HubConnectionState.Disconnecting) {
      return this.hubConnection;
    }

    const version = store.sessionVersion;
    const connection = new HubConnectionBuilder()
      .withUrl(new URL("casesHub", store.requestUrl).href, {
        accessTokenFactory: () => store.getAccessToken(),
      })
      .withAutomaticReconnect()
      .build();
    this.hubConnection = connection;
    const isCurrent = () => this.hubConnection === connection &&
      store.sessionVersion === version && store.isCaseLoaded;

    connection.on("ReceiveCaseUpdate", (data: unknown) => {
      if (isCurrent()) store.applyUpdate(data, version);
    });
    connection.on("SessionRevoked", () => {
      if (isCurrent()) store.invalidateSession();
    });
    connection.onreconnected(async () => {
      if (!isCurrent()) {
        await connection.stop();
        return;
      }
      try {
        await store.getAccessToken();
        await connection.invoke("SubscribeToCurrentCase");
      } catch (error) {
        if (isCurrent()) {
          if (authorizationDenied(error)) store.invalidateSession();
          else await this.restartConnection();
        }
      }
    });
    connection.onclose(async () => {
      if (!isCurrent()) return;
      const token = await store.refreshSession();
      if (!token || !isCurrent()) return;
      try {
        await this.restartConnection();
      } catch {
        if (isCurrent()) store.invalidateSession();
      }
    });

    const attempt = (async () => {
      try {
        await connection.start();
        if (!isCurrent()) {
          await connection.stop();
          return null;
        }
        await connection.invoke("SubscribeToCurrentCase");
        this.retryDelay = 2000;
        if (this.retryHandle !== null) window.clearTimeout(this.retryHandle);
        this.retryHandle = null;
        return connection;
      } catch (error) {
        const current = isCurrent();
        if (this.hubConnection === connection) this.hubConnection = null;
        try {
          await connection.stop();
        } catch {
          // A failed handshake can leave the transport already stopped.
        }
        if (current) {
          if (authorizationDenied(error)) store.invalidateSession();
          else this.scheduleRetry(version);
        }
        return null;
      }
    })();
    this.opening = attempt;
    try {
      return await attempt;
    } finally {
      if (this.opening === attempt) this.opening = null;
    }
  }

  async closeConnection() {
    if (this.retryHandle !== null) window.clearTimeout(this.retryHandle);
    this.retryHandle = null;
    this.retryDelay = 2000;
    const connection = this.hubConnection;
    this.hubConnection = null;
    if (connection) {
      try {
        await connection.stop();
      } catch {
        // The transport can already be closing when a visitor switches links.
      }
    }
  }

  private scheduleRetry(version: number) {
    if (this.retryHandle !== null) return;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(this.retryDelay * 2, 30000);
    this.retryHandle = window.setTimeout(() => {
      this.retryHandle = null;
      const store = this.rootStore.casesStore;
      if (store.sessionVersion === version && store.isCaseLoaded) {
        void this.openConnection();
      }
    }, delay);
  }
}
